import React, { useEffect, useState } from "react";
import api, { formatError } from "@/lib/api";
import { toast } from "sonner";
import { IndianRupee, TrendingUp, Package as PackageIcon, Clock } from "lucide-react";

function Stat({ label, value, sub, Icon, tone = "primary" }) {
  const tones = {
    primary: "bg-sarthi-primary",
    green: "bg-green-600",
    amber: "bg-amber-500",
    stone: "bg-stone-600",
  };
  return (
    <div className="sarthi-card p-5">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs font-bold uppercase tracking-wide text-sarthi-muted">{label}</div>
          <div className="mt-1 font-display text-3xl font-extrabold text-sarthi-text">{value}</div>
          {sub && <div className="mt-1 text-sm font-semibold text-sarthi-muted">{sub}</div>}
        </div>
        <div className={`flex h-11 w-11 items-center justify-center rounded-xl ${tones[tone]} text-white shadow-sm`}>
          <Icon className="h-5 w-5" strokeWidth={2.5} />
        </div>
      </div>
    </div>
  );
}

export default function AdminRevenue() {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/admin/revenue").then((r) => setData(r.data)).catch((e) => toast.error(formatError(e)));
  }, []);

  if (!data) return <div className="p-10 text-center text-sarthi-muted">Loading…</div>;

  const total = data.total_revenue_inr || 0;
  const byPackage = Object.entries(data.by_package || {})
    .map(([id, v]) => ({ id, ...v }))
    .sort((a, b) => b.total - a.total);

  return (
    <div className="mx-auto max-w-6xl px-4 py-10 sm:px-6" data-testid="admin-revenue-page">
      <h1 className="font-display text-3xl font-extrabold text-sarthi-text sm:text-4xl">Revenue</h1>
      <p className="mt-1 text-base font-medium text-sarthi-muted">Live payment metrics across all Sarthi contractors.</p>

      <div className="mt-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Stat label="Total revenue" value={<span data-testid="total-revenue">₹{total.toFixed(0)}</span>} Icon={IndianRupee} />
        <Stat label="Paid transactions" value={data.total_paid_count} Icon={TrendingUp} tone="green" />
        <Stat label="Pending / unpaid" value={data.pending_count} Icon={Clock} tone="amber" />
        <Stat label="Active packages" value={byPackage.length} Icon={PackageIcon} tone="stone" />
      </div>

      <h2 className="mt-10 font-display text-2xl font-extrabold text-sarthi-text">Revenue by package</h2>
      {byPackage.length === 0 ? (
        <div className="sarthi-card mt-4 p-6 text-center text-sarthi-muted">No paid transactions yet.</div>
      ) : (
        <div className="sarthi-card mt-4 overflow-hidden p-0">
          <table className="w-full text-sm">
            <thead className="bg-sarthi-accent text-left">
              <tr>
                <th className="px-4 py-3 font-bold uppercase tracking-wide text-sarthi-muted">Package</th>
                <th className="px-4 py-3 font-bold uppercase tracking-wide text-sarthi-muted">Sales</th>
                <th className="px-4 py-3 font-bold uppercase tracking-wide text-sarthi-muted">Revenue</th>
                <th className="px-4 py-3 font-bold uppercase tracking-wide text-sarthi-muted">Share</th>
              </tr>
            </thead>
            <tbody>
              {byPackage.map((p) => {
                const share = total > 0 ? (p.total / total) * 100 : 0;
                return (
                  <tr key={p.id} data-testid={`revenue-row-${p.id}`} className="border-t border-sarthi-border">
                    <td className="px-4 py-3 font-bold text-sarthi-text">{p.label}</td>
                    <td className="px-4 py-3 text-sarthi-text">{p.count}</td>
                    <td className="px-4 py-3 font-bold text-sarthi-text">₹{p.total.toFixed(0)}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="h-2 w-24 rounded-full bg-sarthi-accent">
                          <div className="h-2 rounded-full bg-sarthi-primary" style={{ width: `${share}%` }} />
                        </div>
                        <span className="text-xs font-bold text-sarthi-muted">{share.toFixed(0)}%</span>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <h2 className="mt-10 font-display text-2xl font-extrabold text-sarthi-text">Recent paid transactions</h2>
      {(data.recent || []).length === 0 ? (
        <div className="sarthi-card mt-4 p-6 text-center text-sarthi-muted">No paid transactions yet.</div>
      ) : (
        <div className="sarthi-card mt-4 overflow-hidden p-0">
          <table className="w-full text-sm">
            <thead className="bg-sarthi-accent text-left">
              <tr>
                <th className="px-4 py-3 font-bold uppercase tracking-wide text-sarthi-muted">When</th>
                <th className="px-4 py-3 font-bold uppercase tracking-wide text-sarthi-muted">Package</th>
                <th className="px-4 py-3 font-bold uppercase tracking-wide text-sarthi-muted">Amount</th>
                <th className="px-4 py-3 font-bold uppercase tracking-wide text-sarthi-muted">Session</th>
              </tr>
            </thead>
            <tbody>
              {data.recent.map((tx) => (
                <tr key={tx.session_id} className="border-t border-sarthi-border">
                  <td className="px-4 py-3 text-sarthi-muted">{tx.updated_at ? new Date(tx.updated_at).toLocaleString() : ""}</td>
                  <td className="px-4 py-3 font-bold text-sarthi-text">{tx.package_label}</td>
                  <td className="px-4 py-3 font-bold text-sarthi-text">₹{tx.amount}</td>
                  <td className="px-4 py-3 font-mono text-xs text-sarthi-muted">{tx.session_id.slice(0, 20)}…</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
